import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, CalendarOff, Check, X, Undo2 } from 'lucide-react';
import apiClient from '../../api/axios';
import AddLeaveModal from '../AddLeaveModal';
import { useSubscription } from '../../contexts/SubscriptionContext';

const STATUS_TABS = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'];
const PAGE_SIZE = 50;

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function mondayOf(ymd) {
  const d = new Date(ymd + 'T00:00:00');
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function formatDate(ymd) {
  if (!ymd) return '';
  const d = new Date(ymd + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateRange(start, end) {
  if (start === end) return formatDate(start);
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function formatInstant(instant) {
  if (!instant) return null;
  const d = new Date(instant);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function StatusPill({ status, t }) {
  const styles = {
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    APPROVED: 'bg-green-50 text-green-700 border-green-200',
    REJECTED: 'bg-red-50 text-red-700 border-red-200',
    CANCELLED: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.CANCELLED}`}>
      {t(`leave.status.${status}`)}
    </span>
  );
}

function LeaveTab({ employees, onToast }) {
  const { t } = useTranslation('team');
  const { t: tCommon } = useTranslation('common');
  const { isGated } = useSubscription();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const [whosOffDate, setWhosOffDate] = useState(todayYMD());
  const [whosOffEntries, setWhosOffEntries] = useState([]);
  const [whosOffLoading, setWhosOffLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/leave-requests', {
        params: { status: statusFilter, page, size: PAGE_SIZE },
      });
      setRows(res.data?.content ?? []);
      setTotalPages(res.data?.totalPages ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || t('leave.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, t]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchWhosOff = useCallback(async (date) => {
    setWhosOffLoading(true);
    try {
      const res = await apiClient.get('/api/schedules/week/leave', {
        params: { weekStart: mondayOf(date) },
      });
      const entries = (res.data ?? []).filter(e => date >= e.startDate && date <= e.endDate);
      setWhosOffEntries(entries);
    } catch {
      setWhosOffEntries([]);
    } finally {
      setWhosOffLoading(false);
    }
  }, []);

  useEffect(() => { fetchWhosOff(whosOffDate); }, [whosOffDate, fetchWhosOff]);

  const changeStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const approve = async (id) => {
    if (isGated) return;
    setBusyId(id);
    try {
      await apiClient.post(`/api/leave-requests/${id}/approve`);
      onToast(t('leave.toast.approved'));
      fetchList();
      fetchWhosOff(whosOffDate);
    } catch (err) {
      onToast(err.response?.data?.message || t('leave.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    if (isGated) return;
    if (!window.confirm(t('leave.confirm.reject'))) return;
    setBusyId(id);
    try {
      await apiClient.post(`/api/leave-requests/${id}/reject`);
      onToast(t('leave.toast.rejected'));
      fetchList();
    } catch (err) {
      onToast(err.response?.data?.message || t('leave.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (id) => {
    if (isGated) return;
    if (!window.confirm(t('leave.confirm.revoke'))) return;
    setBusyId(id);
    try {
      await apiClient.post(`/api/leave-requests/${id}/revoke`);
      onToast(t('leave.toast.revoked'));
      fetchList();
      fetchWhosOff(whosOffDate);
    } catch (err) {
      onToast(err.response?.data?.message || t('leave.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleAdded = () => {
    setAddOpen(false);
    onToast(t('leave.toast.added'));
    fetchList();
    fetchWhosOff(whosOffDate);
  };

  const overlapHint = (row) => {
    if (row.status !== 'PENDING') return null;
    const count = whosOffEntries.filter(e =>
      e.employeeId !== row.employeeId
      && row.startDate <= e.endDate && row.endDate >= e.startDate
    ).length;
    return count > 0 ? t('leave.othersOff', { count }) : null;
  };

  return (
    <div className="px-6 pb-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main list */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              {STATUS_TABS.map(s => (
                <button
                  key={s}
                  onClick={() => changeStatusFilter(s)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    statusFilter === s ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t(`leave.filters.${s}`)}
                </button>
              ))}
            </div>
            <button
              onClick={() => setAddOpen(true)}
              disabled={isGated}
              title={isGated ? tCommon('gatedAction.tooltip') : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={16} />
              {t('leave.addLeave')}
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-gray-200 bg-white">
              <CalendarOff size={36} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600">{t('leave.empty')}</p>
            </div>
          )}

          {(loading || rows.length > 0) && (
            <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.staff')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.dates')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.days')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.requested')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leave.columns.decided')}</th>
                      <th className="px-4 py-3 w-40" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading && [...Array(3)].map((_, i) => (
                      <tr key={i}>
                        <td colSpan={7} className="px-4 py-3">
                          <div className="h-3 rounded bg-gray-100 animate-pulse" />
                        </td>
                      </tr>
                    ))}
                    {!loading && rows.map(row => {
                      const hint = overlapHint(row);
                      return (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {row.employeeName || '—'}
                            {hint && (
                              <div className="text-xs font-normal text-amber-600 mt-0.5">{hint}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{formatDateRange(row.startDate, row.endDate)}</td>
                          <td className="px-4 py-3 text-gray-700">{row.dayCount}</td>
                          <td className="px-4 py-3"><StatusPill status={row.status} t={t} /></td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatInstant(row.createdAt) || '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {row.decidedAt ? (
                              <>
                                {formatInstant(row.decidedAt)}
                                <div className="text-gray-400">{row.decidedBy ? t('leave.decidedVia.web') : t('leave.decidedVia.whatsapp')}</div>
                              </>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              {row.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={() => approve(row.id)}
                                    disabled={busyId === row.id || isGated}
                                    title={isGated ? tCommon('gatedAction.tooltip') : undefined}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                                  >
                                    <Check size={13} />
                                    {t('leave.actions.approve')}
                                  </button>
                                  <button
                                    onClick={() => reject(row.id)}
                                    disabled={busyId === row.id || isGated}
                                    title={isGated ? tCommon('gatedAction.tooltip') : undefined}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                                  >
                                    <X size={13} />
                                    {t('leave.actions.reject')}
                                  </button>
                                </>
                              )}
                              {row.status === 'APPROVED' && (
                                <button
                                  onClick={() => revoke(row.id)}
                                  disabled={busyId === row.id || isGated}
                                  title={isGated ? tCommon('gatedAction.tooltip') : undefined}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                                >
                                  <Undo2 size={13} />
                                  {t('leave.actions.revoke')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40"
                  >
                    {t('leave.pagination.prev')}
                  </button>
                  <span>{t('leave.pagination.page', { page: page + 1, totalPages })}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40"
                  >
                    {t('leave.pagination.next')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Who's off panel */}
        <div className="w-full lg:w-72 shrink-0">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('leave.whosOff.title')}</h3>
            <input
              type="date"
              value={whosOffDate}
              onChange={e => setWhosOffDate(e.target.value)}
              className="w-full mb-3 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
            />
            {whosOffLoading && <div className="h-3 rounded bg-gray-100 animate-pulse" />}
            {!whosOffLoading && whosOffEntries.length === 0 && (
              <p className="text-xs text-gray-400">{t('leave.whosOff.empty')}</p>
            )}
            {!whosOffLoading && whosOffEntries.length > 0 && (
              <ul className="space-y-1.5">
                {whosOffEntries.map(e => (
                  <li key={e.employeeId} className="text-sm text-gray-700 flex items-center gap-2">
                    <CalendarOff size={13} className="text-amber-500 shrink-0" />
                    {e.employeeName}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <AddLeaveModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        employees={employees}
        onAdded={handleAdded}
      />
    </div>
  );
}

export default LeaveTab;
